import { useEffect, useRef, useState } from "react";
import type { ComponentProps, CSSProperties, FC } from "react";
import {
  Button as MuiButton,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import BaseButton from "../../../components/EcommerceActionButton/Button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../components/Sheet";
import useCompanyStore from "../../../Store/supplierCompany/companyData";
import {
  statusLabels,
  validationLabels,
  validationBadgeClass,
} from "../statusLabels";
import { DISPATCH_METHODS, methodLabels } from "../methods";
import styles from "../DispatchesPage.module.scss";
import { useGetInventory } from "../../../utils/react-query/queryInventory/queries";
import { useGetReservation } from "../../../utils/react-query/queryReservations/queries";
import type {
  CreateDispatchPayload,
  UpdateDispatchPayload,
  Dispatch,
  DispatchMethod,
  DispatchStatus,
  DispatchValidationStatus,
} from "../types";

// Ecommerce Button forwards extra props to the underlying <button> but its
// ButtonProps type omits `style`; extend it locally for the fallback below.
type BrandedButtonProps = ComponentProps<typeof BaseButton> & {
  style?: CSSProperties;
};
const Button: FC<BrandedButtonProps> = BaseButton;

interface DispatchFormProps {
  open: boolean;
  /** If provided, the form is in edit mode and submits via onUpdate (PUT, never POST) */
  dispatch?: Dispatch | null;
  /** Edit mode only: when false the Estado field is read-only (dispatches:change_status) */
  canChangeStatus?: boolean;
  onClose: () => void;
  onCreate?: (payload: CreateDispatchPayload) => Promise<void>;
  onUpdate?: (payload: UpdateDispatchPayload) => Promise<void>;
}

interface ItemRow {
  product_id: number;
  storage_address_id: number;
  quantity: number;
}

const emptyItem: ItemRow = { product_id: 0, storage_address_id: 0, quantity: 0 };

/** Creation default, matches the backend default */
const DEFAULT_DISPATCH_STATUS: DispatchStatus = "order_preparation";

export function DispatchForm({
  open,
  dispatch,
  canChangeStatus = false,
  onClose,
  onCreate,
  onUpdate,
}: DispatchFormProps) {
  const isEditMode = !!dispatch;
  // Logistic fields (method/status/date/guide/remission/invoice/convention/
  // duration) are hidden on create and filled later in edit mode.
  const showLogisticsFields = isEditMode;
  const { companyPrimaryColor } = useCompanyStore();
  const { data: inventory, isLoading: isLoadingInventory } = useGetInventory();

  const [reservationId, setReservationId] = useState(
    dispatch?.reservation_id != null ? String(dispatch.reservation_id) : ""
  );
  const [quotation, setQuotation] = useState(
    dispatch?.quote_number ?? dispatch?.reservation?.quote_number ?? ""
  );
  // Quotation of the typed reservation, used as reference + match validation.
  // Quotation module doesn't exist yet (TPP pending): free text is allowed
  // when there is no reservation.
  const reservationNumericId = /^\d+$/.test(reservationId.trim())
    ? parseInt(reservationId.trim(), 10)
    : 0;
  const { data: reservationDetail } = useGetReservation(reservationNumericId);
  const reservationQuote = reservationDetail?.quote_number ?? null;
  const prevReservationQuote = useRef<string | null>(null);
  useEffect(() => {
    // Auto-fill from the reservation while the field is empty or untouched
    // (still showing the previous reference). Manual edits are preserved.
    setQuotation((prev) =>
      reservationQuote &&
      (prev === "" || prev === prevReservationQuote.current)
        ? reservationQuote
        : prev
    );
    prevReservationQuote.current = reservationQuote;
  }, [reservationQuote]);
  const [method, setMethod] = useState<string>(dispatch?.method ?? "");
  const [status, setStatus] = useState<DispatchStatus>(
    dispatch?.status ?? DEFAULT_DISPATCH_STATUS
  );
  const [recipientName, setRecipientName] = useState(dispatch?.recipient_name ?? "");
  const [recipientPhone, setRecipientPhone] = useState(dispatch?.recipient_phone ?? "");
  const [deliveryAddress, setDeliveryAddress] = useState(dispatch?.delivery_address ?? "");
  const [notes, setNotes] = useState(dispatch?.notes ?? "");
  // Touched tracking so the "required" error shows after interacting, not on
  // first paint (mirrors the DispatchValidationDialog pattern).
  const [notesTouched, setNotesTouched] = useState(false);
  const [dispatchDate, setDispatchDate] = useState(dispatch?.dispatch_date ?? "");
  const [trackingGuide, setTrackingGuide] = useState(dispatch?.tracking_guide ?? "");
  const [remissionCode, setRemissionCode] = useState(dispatch?.remission_code ?? "");
  const [invoiceNumber, setInvoiceNumber] = useState(dispatch?.invoice_number ?? "");
  const [convention, setConvention] = useState(dispatch?.convention ?? "");
  const [durationDays, setDurationDays] = useState(
    dispatch?.duration_days != null ? String(dispatch.duration_days) : ""
  );
  const [items, setItems] = useState<ItemRow[]>(
    dispatch?.items?.map((i) => ({
      product_id: i.product_id,
      storage_address_id: i.storage_address_id,
      quantity: i.quantity,
    })) ?? [{ ...emptyItem }]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMethodValid =
    method.trim().length > 0 &&
    (DISPATCH_METHODS as string[]).includes(method.trim());
  const recipientTrimmed = recipientName.trim();
  const isRecipientValid =
    recipientTrimmed.length >= 1 && recipientTrimmed.length <= 255;
  const quotationTrimmed = quotation.trim();
  const isQuotationValid =
    quotationTrimmed.length >= 1 && quotationTrimmed.length <= 50;
  const isQuotationMismatch =
    !!reservationQuote &&
    quotationTrimmed !== "" &&
    quotationTrimmed !== reservationQuote;
  const isItemsValid =
    items.length > 0 &&
    items.every(
      (item) =>
        item.product_id > 0 &&
        item.storage_address_id > 0 &&
        Number.isInteger(item.quantity) &&
        item.quantity >= 1
    );
  const durationTrimmed = durationDays.trim();
  const isDurationValid =
    durationTrimmed === "" ||
    (/^\d+$/.test(durationTrimmed) && parseInt(durationTrimmed, 10) >= 0);
  // Observations by role (S1/S3): every stage must carry its observation —
  // the advisor on create, Logística on edit. Enforced here in the form
  // rather than in the Joi schema so the API keeps accepting partial PUTs.
  const notesTrimmed = notes.trim();
  const isNotesValid = notesTrimmed.length >= 1 && notesTrimmed.length <= 500;

  // Read-only Validación: edit mode shows the backend value; create mode is
  // derived from the linked reservation (non-empty numeric id => reserved).
  // It is never part of the submitted payload.
  const validationStatus: DispatchValidationStatus = isEditMode
    ? dispatch?.validation_status ?? "pending"
    : /^\d+$/.test(reservationId.trim())
    ? "reserved"
    : "pending";

  // Edit mode uses PUT header-only (backend ignores items, they are
  // immutable after create), so items never block Guardar in edit mode.
  // The list endpoint omits items; the Page loads the detail on edit.
  const canConfirm =
    (!isEditMode || isMethodValid) &&
    isRecipientValid &&
    isQuotationValid &&
    !isQuotationMismatch &&
    isNotesValid &&
    (isEditMode || isItemsValid) &&
    (!isEditMode || isDurationValid) &&
    !isLoadingInventory &&
    !isSubmitting;

  // Local fallback (F7b): the company store is empty in the Dispatches
  // context, so brand the primary action with MUI default primary blue.
  const primaryFallbackStyle = companyPrimaryColor
    ? undefined
    : { backgroundColor: "#3f51b5" };

  const handleItemChange = (index: number, field: keyof ItemRow, value: number) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, { ...emptyItem }]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        // Edit mode uses PUT: never POST, never send reservation_id/items.
        // `status` is only sent when the caller holds dispatches:change_status;
        // the backend rejects the change otherwise.
        if (!onUpdate) return;
        const payload: UpdateDispatchPayload = {
          method: method.trim() as DispatchMethod,
          ...(canChangeStatus ? { status } : {}),
          recipient_name: recipientTrimmed,
          quote_number: quotationTrimmed,
          recipient_phone: recipientPhone.trim() || null,
          delivery_address: deliveryAddress.trim() || null,
          notes: notes.trim() || null,
          dispatch_date: dispatchDate.trim() || null,
          tracking_guide: trackingGuide.trim() || null,
          remission_code: remissionCode.trim() || null,
          invoice_number: invoiceNumber.trim() || null,
          convention: convention.trim() || null,
          duration_days:
            durationTrimmed === "" ? null : parseInt(durationTrimmed, 10),
        };
        await onUpdate(payload);
        return;
      }
      if (!onCreate) return;
      const reservationTrimmed = reservationId.trim();
      // Create mode sends only the base header: method/status/logistics are
      // filled later by Logística/Bodega in edit mode.
      const payload: CreateDispatchPayload = {
        reservation_id:
          reservationTrimmed === "" ? null : parseInt(reservationTrimmed, 10),
        recipient_name: recipientTrimmed,
        quote_number: quotationTrimmed,
        recipient_phone: recipientPhone.trim() || null,
        delivery_address: deliveryAddress.trim() || null,
        notes: notes.trim() || null,
        items: items.map((item) => ({
          product_id: item.product_id,
          storage_address_id: item.storage_address_id,
          quantity: item.quantity,
        })),
      };
      await onCreate(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>
            {isEditMode ? "Editar Despacho" : "Nuevo Despacho"}
          </SheetTitle>
          <p
            style={{ fontSize: 14, color: "#6b7280", margin: "4px 0 0" }}
          >
            {isEditMode
              ? "Modifica los datos del despacho seleccionado."
              : "Completa la información para registrar un nuevo despacho."}
          </p>
        </SheetHeader>

        <div style={{ padding: 24 }}>
        <TextField
          label="Reserva (opcional)"
          variant="outlined"
          fullWidth
          margin="normal"
          value={reservationId}
          onChange={(e) => setReservationId(e.target.value)}
          placeholder="ID de reserva o vacío"
          inputProps={{ "aria-label": "reservation-id", inputMode: "numeric" }}
          helperText="Vacío = despacho manual sin reserva"
        />

        <TextField
          label="Cotización *"
          variant="outlined"
          fullWidth
          margin="normal"
          value={quotation}
          onChange={(e) => setQuotation(e.target.value)}
          placeholder="Número de cotización"
          inputProps={{ maxLength: 50, "aria-label": "quote-number" }}
          error={isQuotationMismatch || quotation.length > 50}
          helperText={
            isQuotationMismatch
              ? `Debe coincidir con la reserva (${reservationQuote})`
              : reservationQuote
              ? `De la reserva: ${reservationQuote}`
              : "Requerida, máximo 50 caracteres"
          }
        />

        {showLogisticsFields && (
          <>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Método de entrega *"
              variant="outlined"
              fullWidth
              margin="normal"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "method" }}
            >
              <option value="">Seleccionar método</option>
              {DISPATCH_METHODS.map((m) => (
                <option key={m} value={m}>
                  {methodLabels[m]}
                </option>
              ))}
            </TextField>

            <TextField
              select
              SelectProps={{ native: true }}
              label="Estado"
              variant="outlined"
              fullWidth
              margin="normal"
              value={status}
              onChange={(e) => setStatus(e.target.value as DispatchStatus)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "status" }}
              disabled={isEditMode && !canChangeStatus}
              helperText={
                isEditMode && !canChangeStatus
                  ? "Sólo lectura: requiere el permiso cambiar estado"
                  : undefined
              }
            >
              {(Object.keys(statusLabels) as DispatchStatus[]).map((s) => (
                <option key={s} value={s}>
                  {statusLabels[s]}
                </option>
              ))}
            </TextField>
          </>
        )}

        <TextField
          label="Nombre del cliente *"
          variant="outlined"
          fullWidth
          margin="normal"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          placeholder="Nombre de quien recibe"
          inputProps={{ maxLength: 255, "aria-label": "recipient-name" }}
          error={recipientName.length > 255}
          helperText={
            recipientName.length > 255
              ? "Máximo 255 caracteres"
              : "Requerido, máximo 255 caracteres"
          }
        />

        <TextField
          label="Teléfono del cliente"
          variant="outlined"
          fullWidth
          margin="normal"
          value={recipientPhone}
          onChange={(e) => setRecipientPhone(e.target.value)}
          inputProps={{ maxLength: 20, "aria-label": "recipient-phone" }}
        />

        <TextField
          label="Ciudad / Dirección de entrega"
          variant="outlined"
          fullWidth
          margin="normal"
          value={deliveryAddress}
          onChange={(e) => setDeliveryAddress(e.target.value)}
          placeholder="Ciudad y dirección"
          inputProps={{ "aria-label": "delivery-address" }}
        />

        <div style={{ marginTop: 16 }}>
          <Typography
            variant="body2"
            style={{ fontSize: 12, color: "#6b7280" }}
          >
            Validación
          </Typography>
          <div style={{ marginTop: 4 }}>
            <span
              className={`${styles.badge} ${validationBadgeClass[validationStatus]}`}
            >
              {validationLabels[validationStatus]}
            </span>
          </div>
        </div>

        {showLogisticsFields && (
          <>
            <TextField
              label="Fecha de despacho"
              type="date"
              variant="outlined"
              fullWidth
              margin="normal"
              value={dispatchDate}
              onChange={(e) => setDispatchDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "dispatch-date" }}
            />

            <TextField
              label="Guía"
              variant="outlined"
              fullWidth
              margin="normal"
              value={trackingGuide}
              onChange={(e) => setTrackingGuide(e.target.value)}
              inputProps={{ maxLength: 255, "aria-label": "tracking-guide" }}
            />

            <TextField
              label="Remisión"
              variant="outlined"
              fullWidth
              margin="normal"
              value={remissionCode}
              onChange={(e) => setRemissionCode(e.target.value)}
              inputProps={{ maxLength: 100, "aria-label": "remission-code" }}
            />

            <TextField
              label="Factura"
              variant="outlined"
              fullWidth
              margin="normal"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              inputProps={{ maxLength: 100, "aria-label": "invoice-number" }}
            />

            <TextField
              label="Convención"
              variant="outlined"
              fullWidth
              margin="normal"
              value={convention}
              onChange={(e) => setConvention(e.target.value)}
              inputProps={{ maxLength: 100, "aria-label": "convention" }}
            />

            <TextField
              label="Duración (días)"
              type="number"
              variant="outlined"
              fullWidth
              margin="normal"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              inputProps={{ min: 0, "aria-label": "duration-days" }}
            />
          </>
        )}

        <TextField
          label="Observación"
          variant="outlined"
          fullWidth
          multiline
          minRows={2}
          margin="normal"
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setNotesTouched(true);
          }}
          onBlur={() => setNotesTouched(true)}
          error={notesTouched && !isNotesValid}
          helperText={
            notesTouched && !isNotesValid
              ? "La observación es obligatoria"
              : "Observación propia de esta etapa; queda en el historial"
          }
          inputProps={{ "aria-label": "notes" }}
        />

        <Typography variant="subtitle2" style={{ marginTop: 16 }}>
          Items
        </Typography>

        {items.map((item, index) => (
          <div key={index} style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Producto"
              variant="outlined"
              size="small"
              value={item.product_id || ""}
              onChange={(e) =>
                handleItemChange(index, "product_id", parseInt(e.target.value, 10) || 0)
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": `product-id-${index}` }}
              disabled={isLoadingInventory}
              style={{ minWidth: 180, flex: 1 }}
            >
              <option value={0}>Seleccionar producto</option>
              {inventory?.products.map((product: any) => (
                <option key={product.id} value={product.id}>
                  {product.name} ({product.line})
                </option>
              ))}
            </TextField>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Bodega"
              variant="outlined"
              size="small"
              value={item.storage_address_id || ""}
              onChange={(e) =>
                handleItemChange(index, "storage_address_id", parseInt(e.target.value, 10) || 0)
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": `address-id-${index}` }}
              disabled={isLoadingInventory}
              style={{ minWidth: 160, flex: 1 }}
            >
              <option value={0}>Seleccionar bodega</option>
              {inventory?.storageAddresses.map((address: any) => (
                <option key={address.id} value={address.id}>
                  {address.name}
                </option>
              ))}
            </TextField>
            <TextField
              label="Cantidad"
              type="number"
              variant="outlined"
              size="small"
              value={item.quantity || ""}
              onChange={(e) =>
                handleItemChange(index, "quantity", parseInt(e.target.value, 10) || 0)
              }
              inputProps={{ min: 1, "aria-label": `quantity-${index}` }}
              style={{ width: 120 }}
            />
            <IconButton
              aria-label="Eliminar item"
              onClick={() => removeItem(index)}
              disabled={items.length <= 1}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </div>
        ))}

        <MuiButton startIcon={<AddIcon />} onClick={addItem} size="small" style={{ marginTop: 8 }}>
          Agregar item
        </MuiButton>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            padding: "16px 24px",
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <Button
            size="medium"
            color="outlined"
            shape="little-rounded"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            size="medium"
            color="primary"
            shape="little-rounded"
            onClick={handleConfirm}
            disabled={!canConfirm}
            isLoading={isSubmitting}
            style={primaryFallbackStyle}
          >
            {isEditMode ? "Guardar" : "Crear"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default DispatchForm;
